<template>
  <sp-date-field
    ref="dateField"
    v-preserve-properties="propertyBindings(fieldAttrs, spDateFieldProps)"
    class="date-filter"
    v-bind="attributeBindings(fieldAttrs, spDateFieldProps)"
    :open="menuOpen"
    :close-on-content-click="closeOnContentClick"
    :name="name"
    :range="range"
    :disabled="disabled"
    :required="required"
    :display-format="displayFormat"
    :location="location"
    :day-test-id="TestId.finder.dateFilterDay"
    @change="handleChange"
    @update-open="handleUpdateOpen"
  >
    <div slot="activator">
      <slot
        name="activator"
        :label="label"
        :text="activatorTitle"
        :prepend-icon="prependIcon"
        :open="open"
        :input-test-id="TestId.finder.dateFilterInput"
      >
        <DefaultFilterActivator
          :value="activatorTitle"
          :label="label"
          :prepend-icon="prependIcon"
          :input-test-id="TestId.finder.dateFilterInput"
          :disabled="disabled"
          no-divider
          class="date-filter__activator"
          @clear="clearSelectedModel"
        />
      </slot>
    </div>

    <sp-label slot="menu-header" block>
      {{ cardLabel }}
    </sp-label>
  </sp-date-field>
</template>

<script setup>
import { watchImmediate } from "@vueuse/core";
import { computed, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useForm } from "#ce/composables/useForm";
import { toBoolean, trimToUndefined } from "#ce/utils/props";
import { attributeBindings, propertyBindings, vPreserveProperties } from "#ce/utils/property-bindings";
import { props as spDateFieldProps } from "#ce/components/SpDateField/props";
import { props as dateFilterProps } from "#ce/shared/filters/date/components/props";
import { TestId } from "#/test-ids";
import DefaultFilterActivator from "#ce/shared/filters/components/DefaultFilterActivator.ce.vue";

defineOptions({ inheritAttrs: false });

const emit = defineEmits(["update:open", "update:model-value"]);

const attrs = useAttrs();

// fieldAttrs drops `value` from the forwarded attributes: Vue rewrites a custom element's `value`
// on every render, so the seed reaches the field as a property instead.
const fieldAttrs = computed(() => {
  const forwarded = { ...attrs };

  delete forwarded.value;

  return forwarded;
});

const { mirrorFormValue } = useForm();

const props = defineProps(dateFilterProps);

defineExpose({ props });

const menuOpen = ref(toBoolean(props.open));

function adoptOpenAttribute(value) {
  menuOpen.value = toBoolean(value);
}

function emitOpen(value) {
  emit("update:open", value);
}

watch(() => props.open, adoptOpenAttribute);
watch(menuOpen, emitOpen);

const model = ref(props.modelValue);

function adoptModelValue(value) {
  model.value = value;
}

watch(() => props.modelValue, adoptModelValue);

// A modelValue prop sync mirrors silently; only the filter's own updates report upward.
function emitModelValue(value) {
  if (value === props.modelValue) {
    return;
  }
  emit("update:model-value", value);
}

watchImmediate(model, emitModelValue);

mirrorFormValue(model);

const dateField = useTemplateRef("dateField");

// The field's `value` attribute names its default, which it stops adopting once a date has been
// picked, so the selection reaches it as the property.
function pushSelectionToField([field, value]) {
  if (field && field.value !== (value ?? "")) {
    field.value = value ?? "";
  }
}

watchImmediate(() => [dateField.value, model.value], pushSelectionToField);

// displayValue is the field's rendered text: the selection is carried in `valueFormat` and rendered
// in `displayFormat`, which is what the activator shows.
const displayValue = ref();

function adoptDisplayText([text]) {
  displayValue.value = text;
}

watchImmediate(() => [dateField.value?.displayText, model.value], adoptDisplayText);

const activatorTitle = computed(() => trimToUndefined(displayValue.value));

function handleChange({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [value] = detail;

  model.value = value;
}

function clearSelectedModel() {
  if (toBoolean(props.disabled)) {
    return;
  }
  model.value = undefined;
}

function handleUpdateOpen({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [isOpen] = detail;
  menuOpen.value = isOpen;
}
</script>

<style scoped>
.date-filter {
  --sp-comp-popup-width: max-content !important;
  --sp-comp-date-field-popup-padding: 0 var(--sp-comp-date-filter-padding-inline, var(--sp-ref-scale-12));
}

sp-label {
  margin-top: var(--sp-ref-scale-12);
  margin-bottom: var(--sp-ref-scale-8);
}
</style>
